import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';

import classNames from 'classnames';
import { debounce } from 'lodash';
import { useSearchParams } from 'react-router-dom';

type SearchProps = {
  setSearch: (search: string) => void;
  placeholder: string;
  className?: string;
  changeSearchParams?: boolean;
};

const Search: React.FC<SearchProps> = ({
  setSearch,
  placeholder,
  className = '',
  changeSearchParams = true,
}: SearchProps) => {
  const searchRef = useRef<HTMLInputElement>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [searchParams, setSearchParams] = useSearchParams();

  const debounceHandler = useMemo(
    () =>
      debounce((value: string) => {
        if (changeSearchParams) setSearchParams({ search: value });
        setSearch(value);
      }, 1000),
    [setSearch, setSearchParams, changeSearchParams]
  );

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const text = event.target.value;
      setSearchText(text);
      debounceHandler(text);
    },
    [debounceHandler]
  );

  useEffect(() => {
    if (changeSearchParams) {
      const searchValue = searchParams.get('search');
      if (!searchValue) return;
      setSearchText(searchValue);
      setSearch(searchValue || '');
      if (searchRef && searchRef.current) {
        searchRef.current.focus();
      }
    }
  }, [setSearch, searchParams, changeSearchParams]);

  return (
    <input
      ref={searchRef}
      value={searchText}
      className={classNames('input sm:w-1/2 sm:flex-grow-0 flex-grow', className)}
      placeholder={placeholder}
      onChange={handleOnChange}
      data-testid="search"
      autoComplete="off"
    />
  );
};

export default Search;
